// =====================================================
// MEDICAL ACTS ADMIN PAGE - Actos Médicos
// CSS CORREGIDO - Headers, truncate, botones, scroll
// =====================================================

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Stethoscope,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Link2,
  MapPin,
  Check,
  FileSpreadsheet,
  Euro,
  Smile,
  Building2,
  AlertTriangle,
  CircleDollarSign,
  CircleOff,
  Tag,
  FolderTree,
  Layers,
  ArrowRight,
  Inbox,
  CheckCircle,
} from 'lucide-react';
import { medicalActsApi, type MedicalAct, type MedicalActLinkedSite } from '../../api/medical-acts';
import { treatmentGroupsApi, type TreatmentGroupSelect } from '../../api/treatment-groups';
import { subspecialtiesApi } from '../../api/subspecialties';
import { specialtiesApi } from '../../api/specialties';
import { medicalActsImportExport } from '../../api/import-export';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';
import type { OdontogramType, SpecialtySelect, SubspecialtySelect } from '../../types/catalog-admin';
import type { PaginatedResponse } from '../../types/pagination';

type SortField = 'name' | 'code' | 'defaultPrice' | 'createdAt';

interface LocalLinkedSite extends MedicalActLinkedSite {
  selectedSocietyIds: string[];
}

interface MedicalActFormData {
  code: string;
  name: string;
  description: string;
  requiresOdontogram: boolean;
  odontogramType: string;
  defaultPrice: number;
}

const ODONTOGRAM_TYPES: { value: OdontogramType; label: string; description: string }[] = [
  { value: 'ARCADA', label: 'Arcada', description: 'Superior o inferior completa' },
  { value: 'UNIDAD', label: 'Unidad (pieza)', description: 'Diente individual' },
  { value: 'CUADRANTE', label: 'Cuadrante', description: 'Sección de la boca' },
];

interface SubspecialtyWithSpecialty extends SubspecialtySelect {
  specialtyId: string;
  specialtyCode?: string;
  specialtyName?: string;
}

export function MedicalActsAdminPage() {
  const [data, setData] = useState<PaginatedResponse<MedicalAct> & { meta: { unpricedCount?: number } } | null>(null);
  const [specialties, setSpecialties] = useState<SpecialtySelect[]>([]);
  const [subspecialties, setSubspecialties] = useState<SubspecialtyWithSpecialty[]>([]);
  const [treatmentGroups, setTreatmentGroups] = useState<TreatmentGroupSelect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSpecialtyId, setFilterSpecialtyId] = useState<string>('');
  const [filterSubspecialtyId, setFilterSubspecialtyId] = useState<string>('');
  const [filterTreatmentGroupId, setFilterTreatmentGroupId] = useState<string>('');
  const [filterUnpricedOnly, setFilterUnpricedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MedicalAct | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [isClassificationModalOpen, setIsClassificationModalOpen] = useState(false);
  const [classificationItem, setClassificationItem] = useState<MedicalAct | null>(null);
  const [selectedSpecialtyIds, setSelectedSpecialtyIds] = useState<string[]>([]);
  const [selectedSubspecialtyIds, setSelectedSubspecialtyIds] = useState<string[]>([]);
  const [isSavingClassification, setIsSavingClassification] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
  const [groupsItem, setGroupsItem] = useState<MedicalAct | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [isSavingGroups, setIsSavingGroups] = useState(false);
  const [groupsSearch, setGroupsSearch] = useState('');

  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkingItem, setLinkingItem] = useState<MedicalAct | null>(null);
  const [linkedSites, setLinkedSites] = useState<LocalLinkedSite[]>([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);

  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: MedicalAct | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<MedicalActFormData>({
    defaultValues: {
      code: '',
      name: '',
      description: '',
      requiresOdontogram: false,
      odontogramType: '',
      defaultPrice: 0,
    },
  });

  const watchRequiresOdontogram = watch('requiresOdontogram');

  const loadCatalogs = useCallback(async () => {
    try {
      const [specs, subsps, groups] = await Promise.all([
        specialtiesApi.getForSelect(),
        subspecialtiesApi.getForSelect(),
        treatmentGroupsApi.getForSelect(),
      ]);
      setSpecialties(specs);
      setSubspecialties(subsps as SubspecialtyWithSpecialty[]);
      setTreatmentGroups(groups);
    } catch (err) {
      console.error('Error loading catalogs:', err);
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await medicalActsApi.getAllPaginated({
        page,
        limit,
        search: searchQuery || undefined,
        sortBy,
        sortOrder,
        includeInactive: showInactive,
        specialtyId: filterSpecialtyId || undefined,
        subspecialtyId: filterSubspecialtyId || undefined,
        treatmentGroupId: filterTreatmentGroupId || undefined,
        unpricedOnly: filterUnpricedOnly || undefined,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar actos médicos');
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, searchQuery, sortBy, sortOrder, showInactive, filterSpecialtyId, filterSubspecialtyId, filterTreatmentGroupId, filterUnpricedOnly]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({
      code: '',
      name: '',
      description: '',
      requiresOdontogram: false,
      odontogramType: '',
      defaultPrice: 0,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: MedicalAct) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      code: item.code,
      name: item.name,
      description: item.description || '',
      requiresOdontogram: item.requiresOdontogram,
      odontogramType: item.odontogramType || '',
      defaultPrice: item.defaultPrice,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: MedicalActFormData) => {
    setFormError(null);

    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = data?.data.find(
      (item) => item.code.toUpperCase() === codeUpper && item.id !== editingItem?.id
    );

    if (existingItem) {
      setFormError(`Ya existe un acto médico con el código "${codeUpper}"`);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        description: formData.description || undefined,
        requiresOdontogram: formData.requiresOdontogram,
        odontogramType:
          formData.requiresOdontogram && formData.odontogramType
            ? (formData.odontogramType as OdontogramType)
            : null,
        defaultPrice: formData.defaultPrice || 0,
        specialtyIds: editingItem?.specialties.map(s => s.id) || [],
        subspecialtyIds: editingItem?.subspecialties.map(s => s.id) || [],
        treatmentGroupIds: editingItem?.treatmentGroups.map(g => g.id) || [],
      };

      if (editingItem) {
        await medicalActsApi.update(editingItem.id, payload);
        toast.success('Actualizado', `Acto "${formData.name}" actualizado`);
      } else {
        await medicalActsApi.create(payload);
        toast.success('Creado', `Acto "${formData.name}" creado`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      loadData();
    } catch (err: any) {
      setFormError(err.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: MedicalAct) => {
    setConfirmDialog({ isOpen: true, item, action: item.isActive ? 'deactivate' : 'activate' });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await medicalActsApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivado', `Acto "${confirmDialog.item.name}" desactivado`);
      } else {
        await medicalActsApi.reactivate(confirmDialog.item.id);
        toast.success('Activado', `Acto "${confirmDialog.item.name}" activado`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // =====================================================
  // CLASIFICACIÓN
  // =====================================================
  const handleOpenClassificationModal = (item: MedicalAct) => {
    setClassificationItem(item);
    setSelectedSpecialtyIds(item.specialties.map(s => s.id));
    setSelectedSubspecialtyIds(item.subspecialties.map(s => s.id));
    setIsClassificationModalOpen(true);
  };

  const handleCloseClassificationModal = () => {
    if (!isSavingClassification) {
      setIsClassificationModalOpen(false);
      setClassificationItem(null);
      setSelectedSpecialtyIds([]);
      setSelectedSubspecialtyIds([]);
      setShowClearConfirm(false);
    }
  };

  const handleClearClassification = () => {
    setSelectedSpecialtyIds([]);
    setSelectedSubspecialtyIds([]);
    setShowClearConfirm(false);
  };

  const getSubspecialtiesForSpecialty = useCallback((specId: string) => {
    return subspecialties.filter(sub => sub.specialtyId === specId);
  }, [subspecialties]);

  const getSelectedSubspecialtyCount = useCallback((specId: string) => {
    const specSubs = getSubspecialtiesForSpecialty(specId);
    return specSubs.filter(sub => selectedSubspecialtyIds.includes(sub.id)).length;
  }, [getSubspecialtiesForSpecialty, selectedSubspecialtyIds]);

  const handleToggleSpecialty = (specId: string) => {
    const isSelected = selectedSpecialtyIds.includes(specId);
    
    if (isSelected) {
      // Al deseleccionar, quitar también las subespecialidades
      setSelectedSpecialtyIds(prev => prev.filter(id => id !== specId));
      const specSubIds = getSubspecialtiesForSpecialty(specId).map(s => s.id);
      setSelectedSubspecialtyIds(prev => prev.filter(id => !specSubIds.includes(id)));
    } else {
      setSelectedSpecialtyIds(prev => [...prev, specId]);
    }
  };

  const handleToggleSubspecialty = (subId: string) => {
    setSelectedSubspecialtyIds(prev => 
      prev.includes(subId) 
        ? prev.filter(id => id !== subId)
        : [...prev, subId]
    );
  };

  const handleToggleAllSubspecialties = (specId: string) => {
    const specSubs = getSubspecialtiesForSpecialty(specId);
    const specSubIds = specSubs.map(s => s.id);
    const allSelected = specSubIds.every(id => selectedSubspecialtyIds.includes(id));
    
    if (allSelected) {
      setSelectedSubspecialtyIds(prev => prev.filter(id => !specSubIds.includes(id)));
    } else {
      setSelectedSubspecialtyIds(prev => [...new Set([...prev, ...specSubIds])]);
    }
  };

  const handleSaveClassification = async () => {
    if (!classificationItem) return;
    setIsSavingClassification(true);
    try {
      await medicalActsApi.update(classificationItem.id, {
        code: classificationItem.code,
        name: classificationItem.name,
        description: classificationItem.description || undefined,
        requiresOdontogram: classificationItem.requiresOdontogram,
        odontogramType: classificationItem.odontogramType || null,
        defaultPrice: classificationItem.defaultPrice,
        specialtyIds: selectedSpecialtyIds,
        subspecialtyIds: selectedSubspecialtyIds,
        treatmentGroupIds: classificationItem.treatmentGroups.map(g => g.id),
      });
      toast.success('Guardado', 'Clasificación actualizada');
      setIsClassificationModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
    } finally {
      setIsSavingClassification(false);
    }
  };

  // =====================================================
  // GRUPOS
  // =====================================================
  const handleOpenGroupsModal = (item: MedicalAct) => {
    setGroupsItem(item);
    setSelectedGroupIds(item.treatmentGroups.map(g => g.id));
    setGroupsSearch('');
    setIsGroupsModalOpen(true);
  };

  const handleCloseGroupsModal = () => {
    if (!isSavingGroups) {
      setIsGroupsModalOpen(false);
      setGroupsItem(null);
      setSelectedGroupIds([]);
      setGroupsSearch('');
    }
  };

  const filteredAvailableGroups = useMemo(() => {
    const available = treatmentGroups.filter(g => !selectedGroupIds.includes(g.id));
    if (!groupsSearch.trim()) return available;
    const search = groupsSearch.toLowerCase();
    return available.filter(g => 
      g.name.toLowerCase().includes(search) || 
      g.code.toLowerCase().includes(search)
    );
  }, [treatmentGroups, selectedGroupIds, groupsSearch]);

  const selectedGroups = useMemo(() => {
    return treatmentGroups.filter(g => selectedGroupIds.includes(g.id));
  }, [treatmentGroups, selectedGroupIds]);

  const handleAddGroup = (groupId: string) => {
    setSelectedGroupIds(prev => [...prev, groupId]);
  };

  const handleRemoveGroup = (groupId: string) => {
    setSelectedGroupIds(prev => prev.filter(id => id !== groupId));
  };

  const handleSaveGroups = async () => {
    if (!groupsItem) return;
    setIsSavingGroups(true);
    try {
      await medicalActsApi.update(groupsItem.id, {
        code: groupsItem.code,
        name: groupsItem.name,
        description: groupsItem.description || undefined,
        requiresOdontogram: groupsItem.requiresOdontogram,
        odontogramType: groupsItem.odontogramType || null,
        defaultPrice: groupsItem.defaultPrice,
        specialtyIds: groupsItem.specialties.map(s => s.id),
        subspecialtyIds: groupsItem.subspecialties.map(s => s.id),
        treatmentGroupIds: selectedGroupIds,
      });
      toast.success('Guardado', 'Grupos actualizados');
      setIsGroupsModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo guardar');
    } finally {
      setIsSavingGroups(false);
    }
  };

  // =====================================================
  // VINCULAR CENTROS
  // =====================================================
  const handleOpenLinkModal = async (item: MedicalAct) => {
    setLinkingItem(item);
    setIsLinkModalOpen(true);
    setIsLoadingLinks(true);

    try {
      const sites = await medicalActsApi.getLinkedSites(item.id);
      setLinkedSites(
        sites.map((site) => ({
          ...site,
          selectedSocietyIds: site.societies.map((s) => s.id),
        }))
      );
    } catch (err: any) {
      toast.error('Error', 'No se pudieron cargar los centros');
      setIsLinkModalOpen(false);
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const handleCloseLinkModal = () => {
    if (!isSavingLinks) {
      setIsLinkModalOpen(false);
      setLinkingItem(null);
      setLinkedSites([]);
    }
  };

  const handleToggleSiteLink = (siteId: string) => {
    setLinkedSites((prev) =>
      prev.map((site) =>
        site.siteId === siteId
          ? {
              ...site,
              isLinked: !site.isLinked,
              selectedSocietyIds: !site.isLinked ? [] : site.selectedSocietyIds,
            }
          : site
      )
    );
  };

  const handleSitePriceChange = (siteId: string, value: string) => {
    const price = value === '' ? null : parseFloat(value);
    setLinkedSites((prev) =>
      prev.map((site) =>
        site.siteId === siteId ? { ...site, priceOverride: price } : site
      )
    );
  };

  const handleAddSociety = (siteId: string, societyId: string) => {
    if (!societyId) return;
    setLinkedSites((prev) =>
      prev.map((site) =>
        site.siteId === siteId && !site.selectedSocietyIds.includes(societyId)
          ? { ...site, selectedSocietyIds: [...site.selectedSocietyIds, societyId] }
          : site
      )
    );
  };

  const handleRemoveSociety = (siteId: string, societyId: string) => {
    setLinkedSites((prev) =>
      prev.map((site) =>
        site.siteId === siteId
          ? { ...site, selectedSocietyIds: site.selectedSocietyIds.filter((id) => id !== societyId) }
          : site
      )
    );
  };

  const handleSaveLinks = async () => {
    if (!linkingItem) return;
    setIsSavingLinks(true);
    try {
      await medicalActsApi.updateSiteLinks(linkingItem.id, {
        links: linkedSites.map((site) => ({
          siteId: site.siteId,
          isLinked: site.isLinked,
          priceOverride: site.priceOverride,
          societyIds: site.selectedSocietyIds,
        })),
      });
      toast.success('Guardado', 'Vinculaciones actualizadas');
      setIsLinkModalOpen(false);
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron guardar las vinculaciones');
    } finally {
      setIsSavingLinks(false);
    }
  };

  // Helpers
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const getSocietyName = (site: LocalLinkedSite, societyId: string) => {
    const society = site.availableSocieties.find((s) => s.id === societyId);
    return society ? `${society.code} - ${society.name}` : societyId;
  };

  const getAvailableSocietiesToAdd = (site: LocalLinkedSite) => {
    return site.availableSocieties.filter((s) => !site.selectedSocietyIds.includes(s.id));
  };

  const renderPricingIndicator = (item: MedicalAct) => {
    if (item.isUnpriced) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
          <CircleOff className="h-3 w-3" />
          Sin valorar
        </span>
      );
    }
    if (item.hasTariffPrice || item.hasSitePrice || item.defaultPrice > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
          <CircleDollarSign className="h-3 w-3" />
          Valorado
        </span>
      );
    }
    return <span className="text-gray-400 text-xs">-</span>;
  };

  const clearAllFilters = () => {
    setShowInactive(false);
    setSearchQuery('');
    setFilterSpecialtyId('');
    setFilterSubspecialtyId('');
    setFilterTreatmentGroupId('');
    setFilterUnpricedOnly(false);
    setPage(1);
  };

  const hasActiveFilters = showInactive || searchQuery || filterSpecialtyId || filterSubspecialtyId || filterTreatmentGroupId || filterUnpricedOnly;

  const items = data?.data || [];
  const meta = data?.meta;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Actos Médicos</h1>
          <p className="text-sm text-gray-500 mt-1">Gestión del catálogo de actos médicos</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <form onSubmit={handleSearch} className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código o nombre..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </form>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters 
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nuevo acto
            </button>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">Filtros activos:</span>
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Incluye inactivos
                <button onClick={() => { setShowInactive(false); setPage(1); }} className="ml-1.5 hover:text-blue-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button onClick={() => { setSearchQuery(''); setPage(1); }} className="ml-1.5 hover:text-blue-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            {filterSpecialtyId && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Especialidad: {specialties.find(s => s.id === filterSpecialtyId)?.name}
                <button onClick={() => { setFilterSpecialtyId(''); setPage(1); }} className="ml-1.5 hover:text-blue-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            {filterSubspecialtyId && (
              <div className="inline-flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                Subespecialidad: {subspecialties.find(s => s.id === filterSubspecialtyId)?.name}
                <button onClick={() => { setFilterSubspecialtyId(''); setPage(1); }} className="ml-1.5 hover:text-purple-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            {filterTreatmentGroupId && (
              <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                Grupo: {treatmentGroups.find(g => g.id === filterTreatmentGroupId)?.name}
                <button onClick={() => { setFilterTreatmentGroupId(''); setPage(1); }} className="ml-1.5 hover:text-green-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            {filterUnpricedOnly && (
              <div className="inline-flex items-center bg-amber-50 text-amber-700 px-3 py-1 rounded-full text-xs font-medium">
                <AlertTriangle className="h-3 w-3 mr-1" />Sin valorar ({meta?.unpricedCount ?? 0})
                <button onClick={() => { setFilterUnpricedOnly(false); setPage(1); }} className="ml-1.5 hover:text-amber-900"><X className="h-3 w-3" /></button>
              </div>
            )}
            <button onClick={clearAllFilters} className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2">Limpiar todo</button>
          </div>
        )}

        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-gray-100 pt-3 gap-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Especialidad</label>
                <select value={filterSpecialtyId} onChange={(e) => { setFilterSpecialtyId(e.target.value); setPage(1); }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="">Todas</option>
                  {specialties.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Subespecialidad</label>
                <select value={filterSubspecialtyId} onChange={(e) => { setFilterSubspecialtyId(e.target.value); setPage(1); }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="">Todas</option>
                  {subspecialties.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="block text-xs font-medium text-gray-500 mb-1">Grupo</label>
                <select value={filterTreatmentGroupId} onChange={(e) => { setFilterTreatmentGroupId(e.target.value); setPage(1); }} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="">Todos</option>
                  {treatmentGroups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
                </select>
              </div>
              <label className="inline-flex items-center cursor-pointer group pb-1">
                <input type="checkbox" checked={showInactive} onChange={(e) => { setShowInactive(e.target.checked); setPage(1); }} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900">Inactivos</span>
              </label>
              <label className="inline-flex items-center cursor-pointer group pb-1">
                <input type="checkbox" checked={filterUnpricedOnly} onChange={(e) => { setFilterUnpricedOnly(e.target.checked); setPage(1); }} className="h-4 w-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500" />
                <span className="ml-2 text-sm text-amber-600 group-hover:text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />Sin valorar
                  {meta?.unpricedCount !== undefined && meta.unpricedCount > 0 && (<span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">{meta.unpricedCount}</span>)}
                </span>
              </label>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar:</span>
                <select value={`${sortBy}-${sortOrder}`} onChange={(e) => { const [field, order] = e.target.value.split('-'); setSortBy(field as SortField); setSortOrder(order as 'asc' | 'desc'); setPage(1); }} className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600">
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="defaultPrice-asc">Precio (menor)</option>
                  <option value="defaultPrice-desc">Precio (mayor)</option>
                  <option value="createdAt-desc">Más reciente</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ver:</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  {[10, 25, 50].map((n) => (
                    <button key={n} onClick={() => { setLimit(n); setPage(1); }} className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 last:border-r-0 transition-colors ${limit === n ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'}`}>{n}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>)}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {items.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <Stethoscope className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {filterUnpricedOnly ? '¡Excelente! No hay actos sin valorar' : hasActiveFilters ? 'Sin resultados' : 'No hay actos médicos'}
            </h3>
            <p className="text-gray-500">
              {filterUnpricedOnly ? 'Todos los actos tienen precio asignado' : hasActiveFilters ? 'No se encontraron actos con ese criterio' : 'Crea tu primer acto médico'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Código</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Clasificación</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Odontograma</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Precio Base</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Valoración</th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={item.id} className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''} ${item.isUnpriced ? 'bg-amber-50/30' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-lg">{item.code}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`font-medium ${item.isActive ? 'text-gray-900' : 'text-gray-500'}`}>{item.name}</span>
                        {item.description && (<p className="text-xs text-gray-500 truncate max-w-xs">{item.description}</p>)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {item.specialties.map((s) => (<span key={s.id} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{s.code}</span>))}
                          {item.treatmentGroups.map((g) => (<span key={g.id} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">{g.code}</span>))}
                          {item.specialties.length === 0 && item.treatmentGroups.length === 0 && (<span className="text-gray-400 text-xs">Sin clasificar</span>)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.requiresOdontogram ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            <Smile className="h-3 w-3" />{item.odontogramType}
                          </span>
                        ) : (<span className="text-gray-400 text-xs">-</span>)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {item.defaultPrice > 0 ? (<span className="font-medium text-gray-900">{formatPrice(item.defaultPrice)}</span>) : (<span className="text-gray-400 text-xs">Según tarifa</span>)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">{renderPricingIndicator(item)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button onClick={() => handleToggleStatus(item)} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${item.isActive ? 'bg-blue-600' : 'bg-gray-300'}`} role="switch" aria-checked={item.isActive}>
                          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${item.isActive ? 'translate-x-5' : 'translate-x-0'}`}>
                            <Power className={`h-3 w-3 absolute top-1 left-1 ${item.isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleOpenClassificationModal(item)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Especialidades y Subespecialidades"><Layers className="h-4 w-4" /></button>
                          <button onClick={() => handleOpenGroupsModal(item)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded" title="Grupos de tratamiento"><FolderTree className="h-4 w-4" /></button>
                          <button onClick={() => handleOpenLinkModal(item)} className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="Vincular centros"><Link2 className="h-4 w-4" /></button>
                          <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Editar"><Edit2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {isLoading && (<div className="flex items-center justify-center py-8 border-t"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>)}
            {meta && meta.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">Mostrando {(meta.page - 1) * meta.limit + 1} a {Math.min(meta.page * meta.limit, meta.total)} de {meta.total}</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(meta.page - 1)} disabled={!meta.hasPrevPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronLeft className="h-4 w-4" /></button>
                  <span className="text-sm text-gray-700">Página {meta.page} de {meta.totalPages}</span>
                  <button onClick={() => setPage(meta.page + 1)} disabled={!meta.hasNextPage || isLoading} className="p-2 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-50"><ChevronRight className="h-4 w-4" /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL: CREAR/EDITAR */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={editingItem ? 'Editar acto médico' : 'Nuevo acto médico'} subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos básicos'}>
        <form onSubmit={handleSubmit(handleSave)}>
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center"><AlertCircle className="h-4 w-4 text-red-600" /></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-red-800">Error de validación</p><p className="text-sm text-red-600 mt-0.5">{formError}</p></div>
              <button type="button" onClick={() => setFormError(null)} className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Código *</label>
                <input type="text" {...register('code', { required: 'El código es obligatorio', maxLength: { value: 20, message: 'Máximo 20 caracteres' } })} onChange={() => formError && setFormError(null)} className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`} placeholder="ACT001" />
                {errors.code && (<p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.code.message}</p>)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input type="text" {...register('name', { required: 'El nombre es obligatorio' })} className={`w-full px-4 py-2.5 border rounded-xl text-sm ${errors.name ? 'border-red-300' : 'border-gray-200'}`} placeholder="Nombre del acto médico" />
                {errors.name && (<p className="mt-1 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.name.message}</p>)}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
              <textarea {...register('description')} rows={2} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none" placeholder="Descripción opcional..." />
            </div>
            <div className="p-4 bg-blue-50 rounded-xl space-y-4">
              <div className="flex items-center gap-3">
                <Controller name="requiresOdontogram" control={control} render={({ field }) => (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={field.value} onChange={(e) => { field.onChange(e.target.checked); if (!e.target.checked) setValue('odontogramType', ''); }} className="rounded border-blue-300 text-blue-600" />
                    <Smile className="h-5 w-5 text-blue-600" /><span className="font-medium text-blue-900">Requiere odontograma</span>
                  </label>
                )} />
              </div>
              {watchRequiresOdontogram && (
                <div className="ml-7">
                  <p className="text-sm font-medium text-blue-800 mb-3">Tipo de odontograma *</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {ODONTOGRAM_TYPES.map((type) => (
                      <Controller key={type.value} name="odontogramType" control={control} rules={{ required: watchRequiresOdontogram ? 'Selecciona un tipo de odontograma' : false }} render={({ field }) => (
                        <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${field.value === type.value ? 'border-blue-500 bg-blue-100' : 'border-blue-200 bg-white hover:border-blue-300'}`}>
                          <input type="radio" name="odontogramType" value={type.value} checked={field.value === type.value} onChange={() => field.onChange(type.value)} className="mt-0.5 text-blue-600 focus:ring-blue-500" />
                          <div><span className="font-medium text-blue-900 block">{type.label}</span><span className="text-xs text-blue-600">{type.description}</span></div>
                        </label>
                      )} />
                    ))}
                  </div>
                  {errors.odontogramType && (<p className="mt-2 text-xs text-red-500"><AlertCircle className="h-3 w-3 inline mr-1" />{errors.odontogramType.message}</p>)}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Precio base<span className="text-xs text-gray-400 ml-2">(opcional, se puede definir en tarifa)</span></label>
              <div className="relative max-w-xs">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="number" step="0.01" min="0" {...register('defaultPrice', { valueAsNumber: true })} className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm" placeholder="0.00" />
              </div>
            </div>
            {!editingItem && (
              <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center"><Tag className="h-4 w-4 text-gray-600" /></div>
                  <div><p className="text-sm font-medium text-gray-700">Clasificación del acto</p><p className="text-xs text-gray-500 mt-1">Una vez creado el acto, podrás asignarle especialidades, subespecialidades y grupos de tratamiento usando los botones de acción en la tabla.</p></div>
                </div>
              </div>
            )}
          </div>
          <ModalFooter>
            <button type="button" onClick={handleCloseModal} disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"><X className="h-4 w-4" />Cancelar</button>
            <button type="submit" disabled={isSaving} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />{editingItem ? 'Guardar' : 'Crear'}</>}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* =====================================================
          MODAL: CLASIFICACIÓN - DISEÑO SIMPLE CON CHECKBOXES
          ===================================================== */}
      <Modal
        isOpen={isClassificationModalOpen}
        onClose={handleCloseClassificationModal}
        title="Clasificar por especialidad"
        subtitle={classificationItem ? `${classificationItem.code} - ${classificationItem.name}` : ''}
      >
        <div className="space-y-4">
          {/* Header informativo */}
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <Stethoscope className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">Especialidades y subespecialidades</p>
                <p className="text-xs text-blue-600">Marca las casillas para asignar</p>
              </div>
            </div>
            {selectedSpecialtyIds.length > 0 && (
              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="text-xs text-blue-600 hover:text-red-500 px-2 py-1 hover:bg-white rounded transition-colors"
              >
                Limpiar todo
              </button>
            )}
          </div>

          {/* Lista de especialidades con checkboxes */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="max-h-[350px] overflow-y-auto divide-y divide-gray-100">
              {specialties.map((spec) => {
                const isSelected = selectedSpecialtyIds.includes(spec.id);
                const specSubs = getSubspecialtiesForSpecialty(spec.id);
                const selectedSubCount = getSelectedSubspecialtyCount(spec.id);
                const allSubsSelected = specSubs.length > 0 && specSubs.every(sub => selectedSubspecialtyIds.includes(sub.id));

                return (
                  <div key={spec.id} className={isSelected ? 'bg-blue-50/50' : 'bg-white'}>
                    {/* Fila de especialidad */}
                    <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSpecialty(spec.id)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded" title={spec.code}>
                        {spec.code}
                      </span>
                      <span className="flex-1 text-sm text-gray-900" title={spec.name}>
                        {spec.name}
                      </span>
                      {specSubs.length > 0 && (
                        <span className="text-xs text-gray-400">
                          {isSelected && selectedSubCount > 0 ? (
                            <span className="text-purple-600 font-medium">{selectedSubCount}/{specSubs.length} sub.</span>
                          ) : (
                            <span>{specSubs.length} sub.</span>
                          )}
                        </span>
                      )}
                    </label>

                    {/* Subespecialidades indentadas */}
                    {isSelected && specSubs.length > 0 && (
                      <div className="pl-11 pr-4 pb-3 space-y-1">
                        {/* Botón seleccionar todas */}
                        <div className="flex items-center justify-between py-1 mb-1">
                          <span className="text-[10px] font-medium text-purple-600 uppercase tracking-wider flex items-center gap-1">
                            <Layers className="h-3 w-3" />
                            Subespecialidades
                          </span>
                          <button
                            type="button"
                            onClick={() => handleToggleAllSubspecialties(spec.id)}
                            className="text-[10px] font-medium text-purple-600 hover:text-purple-800 hover:underline"
                          >
                            {allSubsSelected ? 'Quitar todas' : 'Seleccionar todas'}
                          </button>
                        </div>
                        
                        {/* Lista de subespecialidades */}
                        {specSubs.map((sub) => {
                          const isSubSelected = selectedSubspecialtyIds.includes(sub.id);
                          return (
                            <label
                              key={sub.id}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                isSubSelected 
                                  ? 'bg-purple-100 border border-purple-200' 
                                  : 'bg-white border border-gray-200 hover:border-purple-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSubSelected}
                                onChange={() => handleToggleSubspecialty(sub.id)}
                                className="h-3.5 w-3.5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <span className={`text-sm ${isSubSelected ? 'text-purple-900 font-medium' : 'text-gray-700'}`}>
                                {sub.name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Resumen compacto */}
          {selectedSpecialtyIds.length > 0 && (
            <div className="text-xs text-gray-500 px-1">
              <span className="font-medium text-gray-700">{selectedSpecialtyIds.length}</span> especialidad{selectedSpecialtyIds.length !== 1 ? 'es' : ''} 
              {selectedSubspecialtyIds.length > 0 && (
                <> y <span className="font-medium text-purple-600">{selectedSubspecialtyIds.length}</span> subespecialidad{selectedSubspecialtyIds.length !== 1 ? 'es' : ''}</>
              )} seleccionadas
            </div>
          )}
        </div>

        <ModalFooter>
          <button type="button" onClick={handleCloseClassificationModal} disabled={isSavingClassification} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" />Cancelar
          </button>
          <button onClick={handleSaveClassification} disabled={isSavingClassification} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
            {isSavingClassification ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* Confirmación para limpiar clasificación */}
      <ConfirmDialog
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearClassification}
        variant="warning"
        title="¿Limpiar selección?"
        message={`Se quitarán ${selectedSpecialtyIds.length} especialidad${selectedSpecialtyIds.length !== 1 ? 'es' : ''}${selectedSubspecialtyIds.length > 0 ? ` y ${selectedSubspecialtyIds.length} subespecialidad${selectedSubspecialtyIds.length !== 1 ? 'es' : ''}` : ''} seleccionadas.`}
        confirmText="Limpiar"
      />

      {/* =====================================================
          MODAL: GRUPOS - CORREGIDO
          ===================================================== */}
      <Modal
        isOpen={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        title="Grupos de tratamiento"
        subtitle={groupsItem ? `${groupsItem.code} - ${groupsItem.name}` : ''}
      >
        <div className="space-y-4">
          {/* Layout de dos columnas */}
          <div className="grid grid-cols-2 gap-4">
            {/* Columna izquierda: Disponibles */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Disponibles</span>
                <span className="text-xs text-gray-400">{filteredAvailableGroups.length} grupos</span>
              </div>

              {/* Búsqueda */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  value={groupsSearch}
                  onChange={(e) => setGroupsSearch(e.target.value)}
                  placeholder="Filtrar..."
                  className="w-full pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Lista de disponibles con scroll */}
              <div className="border border-gray-200 rounded-lg bg-gray-50 h-[200px] overflow-y-auto">
                {filteredAvailableGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400">
                    {groupsSearch ? (
                      <><Search className="h-6 w-6 mb-1 opacity-50" /><p className="text-xs">Sin resultados</p></>
                    ) : (
                      <><CheckCircle className="h-6 w-6 mb-1 text-green-400" /><p className="text-xs text-green-600">Todos asignados</p></>
                    )}
                  </div>
                ) : (
                  <div className="p-1.5 space-y-1">
                    {filteredAvailableGroups.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        onClick={() => handleAddGroup(group.id)}
                        className="w-full flex items-center gap-2 px-2 py-2 bg-white rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all group text-left"
                      >
                        <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded group-hover:bg-green-100 group-hover:text-green-700 truncate max-w-[70px]" title={group.code}>
                          {group.code}
                        </span>
                        <span className="flex-1 text-xs text-gray-700 truncate" title={group.name}>{group.name}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-green-500 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Columna derecha: Asignados */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Asignados al acto</span>
                {selectedGroups.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedGroupIds([])}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    Quitar todos
                  </button>
                )}
              </div>

              {/* Placeholder para alinear */}
              <div className="h-[30px]" />

              {/* Lista de asignados con scroll */}
              <div className="border-2 border-dashed border-green-200 rounded-lg bg-green-50/30 h-[200px] overflow-y-auto">
                {selectedGroups.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
                      <Inbox className="h-5 w-5 text-green-400" />
                    </div>
                    <p className="text-xs font-medium text-gray-500">Sin grupos asignados</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">← Añade desde la lista</p>
                  </div>
                ) : (
                  <div className="p-1.5 space-y-1">
                    {selectedGroups.map((group, index) => (
                      <div
                        key={group.id}
                        className="flex items-center gap-2 px-2 py-2 bg-white rounded-lg border border-green-200"
                      >
                        <div className="w-5 h-5 bg-green-500 rounded flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="text-[10px] font-bold text-green-700 bg-green-100 px-1.5 py-0.5 rounded truncate max-w-[70px]" title={group.code}>
                          {group.code}
                        </span>
                        <span className="flex-1 text-xs text-gray-700 truncate" title={group.name}>{group.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveGroup(group.id)}
                          className="p-1 hover:bg-red-100 rounded text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ModalFooter>
          <button type="button" onClick={handleCloseGroupsModal} disabled={isSavingGroups} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
            <X className="h-4 w-4" />Cancelar
          </button>
          <button onClick={handleSaveGroups} disabled={isSavingGroups} className="px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
            {isSavingGroups ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
          </button>
        </ModalFooter>
      </Modal>

      {/* MODAL: VINCULAR CENTROS */}
      <Modal isOpen={isLinkModalOpen} onClose={handleCloseLinkModal} title="Vincular a centros" subtitle={linkingItem ? `${linkingItem.code} - ${linkingItem.name}` : ''} size="lg">
        {isLoadingLinks ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {linkedSites.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No hay centros disponibles</p>
              ) : (
                linkedSites.map((site) => (
                  <div key={site.siteId} className={`p-4 rounded-xl border-2 transition-all ${site.isLinked ? 'border-blue-200 bg-blue-50/50' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => handleToggleSiteLink(site.siteId)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all ${site.isLinked ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 hover:border-blue-400'}`}>
                        {site.isLinked && <Check className="h-4 w-4" />}
                      </button>
                      <div className="flex items-center gap-2 flex-1">
                        <MapPin className="h-5 w-5 text-gray-400" />
                        <span className="font-semibold text-gray-900">{site.siteName}</span>
                        <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{site.siteCode}</span>
                      </div>
                    </div>
                    {site.isLinked && (
                      <div className="mt-4 pl-9 space-y-4">
                        <div className="max-w-xs">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Precio específico (opcional)</label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input type="number" step="0.01" min="0" value={site.priceOverride ?? ''} onChange={(e) => handleSitePriceChange(site.siteId, e.target.value)} placeholder={linkingItem && linkingItem.defaultPrice > 0 ? formatPrice(linkingItem.defaultPrice) : 'Según tarifa'} className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg" />
                          </div>
                        </div>
                        {site.availableSocieties.length > 0 && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-2"><Building2 className="h-3.5 w-3.5 inline mr-1" />Sociedades para este acto</label>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {site.selectedSocietyIds.map((societyId) => (
                                <span key={societyId} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                                  {getSocietyName(site, societyId)}
                                  <button type="button" onClick={() => handleRemoveSociety(site.siteId, societyId)} className="hover:bg-blue-200 rounded-full p-0.5"><X className="h-3.5 w-3.5" /></button>
                                </span>
                              ))}
                              {site.selectedSocietyIds.length === 0 && (<span className="text-xs text-gray-500 italic py-1.5">Usará la sociedad por defecto del centro</span>)}
                            </div>
                            {getAvailableSocietiesToAdd(site).length > 0 && (
                              <div className="flex items-center gap-2">
                                <select onChange={(e) => { handleAddSociety(site.siteId, e.target.value); e.target.value = ''; }} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg" defaultValue="">
                                  <option value="" disabled>Añadir sociedad...</option>
                                  {getAvailableSocietiesToAdd(site).map((s) => (<option key={s.id} value={s.id}>{s.code} - {s.name} {s.isDefault && '(por defecto)'}</option>))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
            <ModalFooter>
              <button type="button" onClick={handleCloseLinkModal} disabled={isSavingLinks} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50">Cancelar</button>
              <button onClick={handleSaveLinks} disabled={isSavingLinks} className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2">
                {isSavingLinks ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><Save className="h-4 w-4" />Guardar</>}
              </button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={() => !isProcessing && setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })} onConfirm={handleConfirmToggle} isLoading={isProcessing} variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'} title={confirmDialog.action === 'deactivate' ? '¿Desactivar acto?' : '¿Activar acto?'} message={confirmDialog.action === 'deactivate' ? `El acto "${confirmDialog.item?.name}" será desactivado.` : `El acto "${confirmDialog.item?.name}" será reactivado.`} confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'} />

      <ImportExportModal isOpen={isImportExportOpen} onClose={() => setIsImportExportOpen(false)} title="Actos Médicos" onExport={medicalActsImportExport.export} onDownloadTemplate={medicalActsImportExport.downloadTemplate} onPreview={medicalActsImportExport.preview} onImport={medicalActsImportExport.import} onImportSuccess={loadData} previewColumns={[{ key: 'code', label: 'Código' }, { key: 'name', label: 'Nombre' }, { key: 'defaultPrice', label: 'Precio' }]} />
    </div>
  );
}

export default MedicalActsAdminPage;
